import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tooltip } from 'antd';
import { CheckCircle, CreditCard, Star, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { cn } from '@/lib/utils';
import { checkHasAdequateCredits } from '@/lib/service/dashboard';
import Image from 'next/image';
import type { InputNumberProps } from 'antd';
import { InputNumber } from 'antd';

const SafeIcon = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 2L3 5V9C3 13.55 6.84 17.74 10 18C13.16 17.74 17 13.55 17 9V5L10 2Z"
      fill="#3EC163"
      stroke="#3EC163"
      strokeWidth="1"
    />
    <path d="M8 10L9.5 11.5L12 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

interface OptModalProps {
  open: boolean;
  onCancel: () => void;
  title?: string;
  description?: string;
  type?: string;
  confirm: (emailValidation: boolean, profileCount: number) => void;
  userCreditInfo: any;
  userInfo?: {
    media_name: string;
    follower_count: number;
    following_count: number;
    profile_pic_url: string;
  };
}

export default function OptModal({
  open,
  onCancel,
  title,
  description,
  type,
  confirm,
  userCreditInfo,
  userInfo,
}: OptModalProps) {
  const { t } = useTranslation('common');
  const [emailValidation, setEmailValidation] = useState(false);
  const [profileCount, setProfileCount] = useState(1); // 当前配置的数量
  const maxProfiles = 200; // 最大数量
  const progressPercent = (profileCount / maxProfiles) * 100;
  const estimatedCredits = emailValidation ? profileCount * 2 : profileCount; // 根据是否开启验证计算积分

  const onChange: InputNumberProps['onChange'] = (value) => {
    setProfileCount(value ? Number(value) : 1);
  };
  const inputNumber = () => (
    <InputNumber value={profileCount} min={1} max={maxProfiles} defaultValue={1} onChange={onChange} controls={false} />
  );

  // 滑块拖动相关状态
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const activePointerIdRef = useRef<number | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  //格式化
  const formatNumberLocal = (value: number) => {
    if (value < 1000) {
      return value.toString();
    }
    if (value >= 1000 && value < 1000000) {
      return (value / 1000).toFixed(1) + 'K';
    } else if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    }

    return value.toString();
  };

  // 计算滑块值
  const calculateValue = useCallback(
    (clientX: number) => {
      if (!sliderRef.current) return 0;

      const rect = sliderRef.current.getBoundingClientRect();
      const sliderWidth = rect.width;
      const x = clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / sliderWidth) * 100));
      const value = Math.round((percentage / 100) * maxProfiles);
      return Math.max(1, Math.min(maxProfiles, value)); // 确保值在1-50之间
    },
    [maxProfiles]
  );

  // 拖动中
  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      sliderRef.current?.setPointerCapture?.(e.pointerId);
      activePointerIdRef.current = e.pointerId;
      isDraggingRef.current = true;
      setIsDragging(true);
      const newValue = calculateValue(e.clientX);
      setProfileCount(newValue);
    },
    [calculateValue]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) return;
      e.preventDefault();
      const newValue = calculateValue(e.clientX);
      setProfileCount(newValue);
    },
    [calculateValue]
  );

  const finishPointer = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  const releasePointer = useCallback(() => {
    if (activePointerIdRef.current === null) return;
    sliderRef.current?.releasePointerCapture?.(activePointerIdRef.current);
    activePointerIdRef.current = null;
  }, []);

  const handlePointerUp = useCallback(() => {
    releasePointer();
    finishPointer();
  }, [releasePointer, finishPointer]);

  const handlePointerCancel = useCallback(() => {
    releasePointer();
    finishPointer();
  }, [releasePointer, finishPointer]);

  // 关闭弹窗
  const handleClose = () => {
    onCancel?.();
    setProfileCount(1);
    setEmailValidation(false);
  };

  // 组件卸载时清理事件监听器
  useEffect(() => {
    return () => {
      releasePointer();
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
      }
    };
  }, [releasePointer]);

  const creditNotEnough = estimatedCredits > userCreditInfo?.remaining_credits;

  const onStart = () => {
    if (creditNotEnough) return;
    checkHasAdequateCredits(estimatedCredits).then((res) => {
      console.log(res);
      if (res.data) {
        // 积分是否足够
        confirm(emailValidation, profileCount);
      }
    });
  };

  const isVerifyEmailDisabled = useMemo(() => {
    if (!userCreditInfo?.has_subscription) {
      return true;
    }
    if (userCreditInfo?.expire_time) {
      return new Date(userCreditInfo?.expire_time) < new Date();
    }
    return false;
  }, [userCreditInfo?.has_subscription, userCreditInfo?.expire_time]);

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="lg:max-w-[548px] w-[90vw] p-0 gap-0 [&>button]:hidden border border-[#E3E3E3] shadow-[0_4px_4px_rgba(0,0,0,0.25)] rounded-xl">
        <div className="relative bg-white rounded-xl p-4">
          <button
            onClick={handleClose}
            className="absolute right-6 top-6 w-4 h-4 flex items-center justify-center text-black hover:opacity-60 transition-opacity z-10"
          >
            <X size={16} strokeWidth={2} />
          </button>

          <h2 className="text-xl font-extrabold text-black mb-2">{t(title)}</h2>

          <p className="lg:text-base text-sm text-black opacity-60 lg:mb-4 mb-2">{t(description)}</p>

          {userInfo && (
            <div className="relative flex items-center lg:gap-4 lg:p-6 gap-2 p-2 mb-4 bg-[#F2FAF2] rounded-[12px]">
              <div className="lg:size-16 size-12 rounded-full overflow-hidden flex-shrink-0">
                <Image
                  src={userInfo.profile_pic_url || '/images/layouts/dolphin_radar_favicon.png'}
                  alt="profile"
                  width={64}
                  height={64}
                  className="lg:w-full lg:h-full w-[48px] h-[48px] object-cover"
                />
              </div>
              <div className="flex flex-col">
                <div className="inline-flex items-center mb-1">
                  <p className="text-sm lg:text-base text-black font-bold mr-1 lg:mr-2 ">{userInfo.media_name}</p>
                  <Image
                    src="/images/account_checked.svg"
                    alt="verified"
                    width={20}
                    height={20}
                    className="lg:size-5 size-4 object-cover"
                  />
                </div>

                <div className="flex gap-4 text-xs lg:text-sm text-black/70">
                  {type === 'follower' || type === 'followers' ? (
                    <span>
                      {formatNumberLocal(userInfo.follower_count)} {t('followers')}
                    </span>
                  ) : (
                    <span>
                      {formatNumberLocal(userInfo.following_count)} {t('followings')}
                    </span>
                  )}
                </div>
              </div>
              <div className="lg:flex hidden absolute right-6 w-[148px] h-[36px] bg-[#C3EDC9] rounded-[35px] items-center justify-center gap-1">
                <CheckCircle size={20} className="text-[#38C13D]" />
                <span className="text-base text-[#38C13D] font-extrabold text-center">Account found</span>
              </div>
              <div className="lg:hidden absolute right-2 w-[116px] h-[30px] bg-[#C3EDC9] rounded-[35px] flex items-center justify-center px-1">
                <CheckCircle size={12} className="text-[#38C13D] inline-block" />
                <span className="text-sm text-[#38C13D] font-extrabold text-center inline-block">Account found</span>
              </div>
            </div>
          )}

          <div className="lg:mb-4 mb-2">
            <div className="gap-2 flex items-center lg:mb-3 mb-2">
              <h3 className="lg:text-base text-sm text-black">{t('Number of Profiles to Extract')}</h3>
              <Tooltip
                title={
                  <div className="text-left max-w-[220px]">
                    <p className="text-sm leading-relaxed tracking-tight">
                      {t(
                        `We'll scan up to 200 public profiles per task to ensure fast and reliable results. Email results may vary by profile visibility and contact info. Want to explore more profiles? Let's talk at `
                      )}
                      <a href="mailto:dolphinradar@gmail.com" className="text-[#3DC2FF]">
                        info.dolphinradar@gmail.com
                      </a>
                      .
                    </p>
                  </div>
                }
              >
                <Image
                  src="/images/instagram-email-finder/help_circle.svg"
                  alt="help"
                  width={16}
                  height={16}
                  className="size-4 object-cover"
                />
              </Tooltip>
            </div>
            {/* 输入框区域 */}
            <div className="mb-4">
              <div className="inline-flex items-center gap-2">
                <p className="text-[12px] text-[#0C0C0C] opacity-70">Selected:</p>
                {inputNumber()}
              </div>
            </div>
            {/* 滑块区域 */}
            <div className="bg-[#F7FAFE] rounded-md p-4 relative">
              {/* 进度条 */}
              <div className="h-6 flex items-center justify-center">
                <div
                  ref={sliderRef}
                  className="w-full max-w-[444px] h-1.5 bg-[#E7EDF4] rounded-full relative cursor-pointer"
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerCancel}
                >
                  <div
                    className={`absolute top-0 left-0 h-full bg-[#725CFF] rounded-full ${
                      isDragging ? '' : 'transition-all'
                    }`}
                    style={{ width: `${progressPercent}%` }}
                  />
                  <div
                    className={`absolute top-1/2 -translate-y-1/2 size-4 bg-[#725CFF] rounded-full border-2 border-white shadow-md cursor-grab select-none ${
                      isDragging ? 'cursor-grabbing scale-110' : 'transition-all'
                    }`}
                    style={{ left: `calc(${progressPercent}% - 6px)` }}
                    onPointerDown={handlePointerDown}
                  />

                  <div
                    className={`absolute -top-10  flex justify-center ${isDragging ? '' : 'transition-all'}`}
                    style={{ left: `calc(${progressPercent}% - 17px)` }}
                  >
                    <div className="relative bg-white rounded-md shadow-lg px-3 py-1 flex items-center">
                      <span className="relative text-[#4D4659] font-bold text-sm">{profileCount}</span>
                      {/* 小三角指向滑块 */}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 lg:mb-4 mb-2 relative">
            {/* 安全图标 */}
            <div className="w-5 h-5 flex items-center justify-center">{SafeIcon}</div>

            <span className="text-sm lg:text-base text-black">{t('Email Validation')}</span>

            {/* Premium 标签 */}
            <div
              className="flex items-center gap-1 px-2 py-1 rounded-md bg-gradient-to-r from-[#FF8A30] to-[#FFBC16] cursor-pointer"
              onClick={() => {
                document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                handleClose();
              }}
            >
              <Star size={12} fill="white" stroke="none" />
              <span className="text-xs text-white font-medium">{t('Premium')}</span>
            </div>
            <div className="flex items-center absolute top-0.5 right-0 z-10">
              <Switch
                checked={emailValidation}
                onCheckedChange={setEmailValidation}
                disabled={isVerifyEmailDisabled}
                className="data-[state=checked]:bg-[#38C13D]"
              />
            </div>
          </div>
          <div className="mb-2 lg:mb-4 relative">
            <p className="inline-block text-sm lg:text-base text-black opacity-60 mb-2">
              {t('Verify email deliverability')}
            </p>
            <div className="inline-flex items-center px-3 py-0.5 bg-[#D7F1D8] rounded-md ml-2">
              <span className="text-xs lg:text-sm text-[#199213]">+1/email</span>
            </div>
          </div>

          <div className="bg-[#F7FAFE] rounded-md p-4 lg:mb-6 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 w-[190px] lg:w-auto">
                <div className="size-9 flex-shrink-0 bg-white border border-[#D9D9D9] rounded-md flex items-center justify-center">
                  <CreditCard size={22} className="text-[#725CFF]" />
                </div>
                <div>
                  <h4 className="text-sm lg:text-base text-black mb-0.5">{t('Credit Estimate')}</h4>
                  <p className="text-xs text-black opacity-60">
                    {t("You'll only be charged for what you actually use.")}
                  </p>
                </div>
              </div>
              <div className={cn(`group`, { 'is-error': creditNotEnough })}>
                <div className="text-base text-black font-normal text-right lg:w-auto">
                  <span className="text-[#988DFF] font-bold group-[.is-error]:text-[#FF0C59]">{estimatedCredits}</span>{' '}
                  <span className="text-xs text-gray-500 group-[.is-error]:text-[#FF0C59]">{t('Credits')}</span>
                </div>
                <div
                  className={cn({
                    hidden: !creditNotEnough,
                    'text-[#FF0C59] text-xs': true,
                  })}
                >
                  Not enough
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="lg:flex-[2] flex-1 flex-shrink-0 h-[42px] lg:text-base text-sm font-medium border-[#D9D9D9] hover:bg-gray-50 rounded-full"
              onClick={() => {
                // TODO: 添加积分逻辑
                document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                handleClose();
              }}
            >
              {t('Add Credits')}
            </Button>
            <Button
              className="lg:flex-[3] flex-1 flex-shrink-0 h-[42px] text-base font-semibold text-white hover:opacity-90 rounded-full"
              disabled={estimatedCredits > userCreditInfo?.remaining_credits}
              style={{
                background: 'linear-gradient(180deg, #725CFF 0%, rgba(141, 131, 255, 0.8) 100%)',
                boxShadow: '0px 4px 12px 0px rgba(114, 92, 255, 0.2)',
              }}
              onClick={() => {
                onStart();
                handleClose();
              }}
            >
              {t('Start')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
